import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import AsyncSelect from 'react-select/async';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import LaneService from '../services/LaneService';
import UserService from '../services/UserService';
import Loading from './Loading';
import '../styles/components/admin-explore-city.css';

const formatCityLabel = (city, stateName) => {
    if (!city) return '';
    return stateName ? `${city}, ${stateName}` : city;
};

export const AdminExploreCity = () => {
    const [farelanesUser, setFarelanesUser] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);
    const [cityData, setCityData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const { getAccessTokenSilently, user } = useAuth0();

    const loadOptions = async (inputValue) => {
        try {
            const token = await getAccessTokenSilently();
            const response = await LaneService.getCityOptions(inputValue || '', token);
            return response || [];
        } catch (err) {
            console.error('Error loading city options:', err);
            return [];
        }
    };

    const handleCityChange = async (value) => {
        setSelectedCity(value);
        setCityData(null);
        setError(null);
        if (!value?.id) return;

        setLoading(true);
        try {
            const token = await getAccessTokenSilently();
            const response = await UserService.citySearch(value.id, token);
            setCityData(response.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load city details');
        } finally {
            setLoading(false);
        }
    };

    const callApi = async () => {
        try {
            const token = await getAccessTokenSilently();
            const response = await UserService.me(token, user);
            setFarelanesUser({ firstName: response.data.auth0GivenName, lastName: response.data.auth0FamilyName });
        } catch (err) {
            setError(err?.error);
        }
    };

    useEffect(() => {
        callApi();
    }, []);

    const formatNum = (n) => n != null ? n.toLocaleString() : '—';
    const formatPct = (n) => n != null ? `${n}%` : '—';
    const formatCur = (n) => n != null ? `$${Number(n).toLocaleString()}` : '—';

    const { isLoaded: isMapLoaded } = useJsApiLoader({
        id: 'google-map-script-explore-city',
        googleMapsApiKey: 'AIzaSyDaLZOaYvwAJGsem6pjU8Vg80he7vi2uf0'
    });

    const mapCenter = cityData?.lat != null && cityData?.lng != null
        ? { lat: cityData.lat, lng: cityData.lng }
        : null;

    const onMapLoad = useCallback(() => {}, []);

    return (
        <div className="admin-explore-city">
            {!farelanesUser && (
                <div className="text-center p-5">
                    <Loading />
                </div>
            )}

            {farelanesUser && (
                <div className="explore-city-content">
                    <h2 className="explore-city-header">Explore a City</h2>
                    <p className="explore-city-intro">
                        Select a city to view detailed demographics, housing, income, and more.
                    </p>

                    <div className="city-select-box">
                        <label><b>Select City</b></label>
                        <AsyncSelect
                            cacheOptions
                            defaultOptions
                            loadOptions={loadOptions}
                            value={selectedCity}
                            onChange={handleCityChange}
                            getOptionLabel={(e) => formatCityLabel(e?.city, e?.stateName)}
                            getOptionValue={(e) => e?.id}
                            placeholder="Start typing a city name..."
                            className="city-async-select"
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderRadius: '10px',
                                    border: '2px solid #e0ebe6',
                                    minHeight: '46px',
                                    boxShadow: '0 2px 8px rgba(0,104,56,0.08)'
                                })
                            }}
                        />
                    </div>

                    {loading && (
                        <div className="city-loading">
                            <Loading />
                        </div>
                    )}

                    {error && (
                        <div className="city-error">
                            {error}
                        </div>
                    )}

                    {cityData && !loading && (
                        <div className="city-dashboard">
                            {/* City header - minimal bar */}
                            <div className="city-header-bar">
                                <h1 className="city-name">{cityData.city}, {cityData.stateName}</h1>
                                <div className="city-header-meta">
                                    {cityData.countyName && <span className="meta-item">County: {cityData.countyName}</span>}
                                    {cityData.timeZone && <span className="meta-item">{cityData.timeZone}</span>}
                                    {cityData.ranking != null && <span className="meta-item">Rank #{cityData.ranking}</span>}
                                </div>
                            </div>

                            {/* Map - larger, full width */}
                            {mapCenter && isMapLoaded && (
                                <div className="city-map-wrapper">
                                    <GoogleMap
                                        mapContainerStyle={{ width: '100%', height: '100%', borderRadius: '12px' }}
                                        center={mapCenter}
                                        zoom={11}
                                        onLoad={onMapLoad}
                                        options={{ mapTypeControl: false, streetViewControl: false, fullscreenControl: false, zoomControl: true }}
                                    >
                                        <Marker position={mapCenter} title={`${cityData.city}, ${cityData.stateName}`} />
                                    </GoogleMap>
                                </div>
                            )}

                            {/* Stats Grid */}
                            <div className="city-stats-grid">
                                <div className="stat-card pop">
                                    <div className="stat-value">{formatNum(cityData.population)}</div>
                                    <div className="stat-label">Population</div>
                                </div>
                                <div className="stat-card pop-proper">
                                    <div className="stat-value">{formatNum(cityData.populationProper)}</div>
                                    <div className="stat-label">Population (Proper)</div>
                                </div>
                                <div className="stat-card dens">
                                    <div className="stat-value">{formatNum(cityData.density)}</div>
                                    <div className="stat-label">Density / mi²</div>
                                </div>
                                <div className="stat-card med-age">
                                    <div className="stat-value">{cityData.ageMedian ?? '—'}</div>
                                    <div className="stat-label">Median Age</div>
                                </div>
                            </div>

                            {/* Section: Demographics */}
                            <div className="city-section">
                                <h3>Demographics</h3>
                                <div className="section-grid">
                                    <div className="info-row"><span>Male</span><strong>{formatPct(cityData.male)}</strong></div>
                                    <div className="info-row"><span>Female</span><strong>{formatPct(cityData.femalePercentage)}</strong></div>
                                    <div className="info-row"><span>Married</span><strong>{formatPct(cityData.married)}</strong></div>
                                    <div className="info-row"><span>Family Size</span><strong>{cityData.familySize ?? '—'}</strong></div>
                                    <div className="info-row"><span>White</span><strong>{formatPct(cityData.race_white)}</strong></div>
                                    <div className="info-row"><span>Black</span><strong>{formatPct(cityData.race_black)}</strong></div>
                                    <div className="info-row"><span>Asian</span><strong>{formatPct(cityData.race_asian)}</strong></div>
                                    <div className="info-row"><span>Native</span><strong>{formatPct(cityData.race_native)}</strong></div>
                                    <div className="info-row"><span>Pacific Islander</span><strong>{formatPct(cityData.race_pacific)}</strong></div>
                                    <div className="info-row"><span>Other</span><strong>{formatPct(cityData.race_other)}</strong></div>
                                    <div className="info-row"><span>Multiple</span><strong>{formatPct(cityData.race_multiple)}</strong></div>
                                </div>
                            </div>

                            {/* Section: Income & Housing */}
                            <div className="city-section">
                                <h3>Income & Housing</h3>
                                <div className="section-grid">
                                    <div className="info-row"><span>Median Household Income</span><strong>{formatCur(cityData.householdIncomeMedian)}</strong></div>
                                    <div className="info-row"><span>Six-Figure Households</span><strong>{formatPct(cityData.incomeHouseHoldSixFigure)}</strong></div>
                                    <div className="info-row"><span>Home Ownership</span><strong>{formatPct(cityData.homeOwnership)}</strong></div>
                                    <div className="info-row"><span>Median Home Value</span><strong>{formatCur(cityData.homeValue)}</strong></div>
                                    <div className="info-row"><span>Median Rent</span><strong>{formatCur(cityData.rent_median)}</strong></div>
                                </div>
                            </div>

                            {/* Section: Education & Employment */}
                            <div className="city-section">
                                <h3>Education & Employment</h3>
                                <div className="section-grid">
                                    <div className="info-row"><span>College or Above</span><strong>{formatPct(cityData.educationCollegeOrAbove)}</strong></div>
                                    <div className="info-row"><span>Labor Force Participation</span><strong>{formatPct(cityData.laborForcePArticipation)}</strong></div>
                                    <div className="info-row"><span>Unemployment Rate</span><strong>{formatPct(cityData.unemploymentRate)}</strong></div>
                                </div>
                            </div>

                            {/* Section: Location & Other */}
                            <div className="city-section">
                                <h3>Location & Details</h3>
                                <div className="section-grid">
                                    <div className="info-row"><span>Latitude</span><strong>{cityData.lat ?? '—'}</strong></div>
                                    <div className="info-row"><span>Longitude</span><strong>{cityData.lng ?? '—'}</strong></div>
                                    <div className="info-row"><span>State ID</span><strong>{cityData.stateId ?? '—'}</strong></div>
                                    <div className="info-row"><span>Incorporated</span><strong>{cityData.incorporated != null ? (cityData.incorporated ? 'Yes' : 'No') : '—'}</strong></div>
                                    <div className="info-row"><span>Military</span><strong>{cityData.military != null ? (cityData.military ? 'Yes' : 'No') : '—'}</strong></div>
                                </div>
                            </div>

                            {/* Zips */}
                            {cityData.zips && (
                                <div className="city-section zips-section">
                                    <h3>ZIP Codes</h3>
                                    <div className="zips-list">{cityData.zips}</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default withAuthenticationRequired(AdminExploreCity, {
    onRedirecting: () => <Loading />,
});
